import { Box, Button, ColorsV2, Divider, Flex, Text } from '@electricjs/arc';

import electricLogo from '@/assets/electric_logo_red.svg';
import { writeSelectedOrgAndReload } from '@/pages/OrgSwitcher/utils';
import { OrgSelectionChoice } from '@/types/users';

import { OrgSelectorRow } from './OrgSelectorRow';

type OrgSelectorProps = {
  orgChoices: OrgSelectionChoice[];
  email?: string;
  onUseDifferentAccount: () => void;
};

/**
 * Shown at login instead of silently auto-selecting the first org, when the
 * user's connection requires an org selection and they belong to more than
 * one org (NERV-5621). Selecting an org is a plain local switch - the same
 * sessionStorage + reload mechanism as the existing OrgSwitcher page.
 */
export const OrgSelector = ({
  orgChoices,
  email,
  onUseDifferentAccount,
}: OrgSelectorProps) => {
  const handleSelectOrg = (choice: OrgSelectionChoice) => {
    writeSelectedOrgAndReload(
      choice.id,
      {
        roles: choice.roles,
        employeeId: choice.employeeId,
      },
      true
    );
  };

  const companyWord = orgChoices.length === 1 ? 'company' : 'companies';

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      width="100vw"
      backgroundColor={ColorsV2.BACKGROUND_ALT}
      padding="2.4rem">
      <img
        src={electricLogo}
        alt="Electric"
        height="48px"
        style={{ marginBottom: '3.2rem' }}
      />
      <Box
        display="flex"
        flexDirection="column"
        width="52rem"
        maxWidth="100%"
        backgroundColor={ColorsV2.BACKGROUND_CONTENT}
        borderRadius="0.8rem"
        overflow="hidden">
        <Flex vertical padding="2.4rem" gap="0.4rem">
          <Text variant="heading-2">Choose a company</Text>
          <Text variant="body-2">
            Signed in as <b>{email}</b> · you have access to {orgChoices.length}{' '}
            {companyWord}.
          </Text>
        </Flex>
        <Divider />
        {orgChoices.map((choice, index) => (
          <Box key={choice.id}>
            <OrgSelectorRow
              choice={choice}
              index={index}
              onSelect={() => handleSelectOrg(choice)}
            />
            {index < orgChoices.length - 1 && <Divider />}
          </Box>
        ))}
      </Box>
      <Flex
        alignItems="center"
        justifyContent="center"
        gap="0.4rem"
        paddingTop="2.4rem">
        <Text variant="body-2">Not you?</Text>
        <Button
          id="org-selector-use-different-account"
          variant="text"
          px="0"
          py="0"
          onClick={onUseDifferentAccount}>
          <b>Use a different account</b>
        </Button>
      </Flex>
    </Box>
  );
};

export default OrgSelector;
